import {
  Accessor,
  Component,
  createEffect,
  createSignal,
  For,
  onMount,
  Setter,
  Show,
} from "solid-js";
import "./modal.css";
import { classNames } from "../utils/etc";
import { routerLog } from "~/utils/logging";
import attachPopStateListener, {
  detachPopStateListener,
  popStateListener,
} from "~/utils/popstate_listener";
import { CloseIcon } from "~/assets/svg_icons/svg_icons";

export enum PageAlignment {
  top,
  bottom,
  left,
  right,
  center,
  topRight,
  topLeft,
  bottomRight,
  bottomLeft,
}
export interface CurrentPage {
  name: HubbleRoute;
  builder: Component<{ page?: CurrentPage }>;
  isModal: boolean;
  isOpen: Accessor<boolean>;
  setIsOpen: Setter<boolean>;
  x?: number;
  y?: number;
  alignment?: PageAlignment;
  offset?: { top?: number; left?: number; right?: number; bottom?: number };
  isContentVisible: Accessor<boolean>;
  setIsContentVisible: Setter<boolean>;
  class?: string;
  enterAnimation?: boolean;
  exitAnimation?: boolean;
  disableCloseButton?: boolean;
  preventClose?: boolean;
  zIndex?: number;
  componentProps?: any;
  eventProps?: Object;
}

export const [pages, setPages] = createSignal<CurrentPage[]>([]);

var isModalPopping = 0;

var pushedWhilePopping: CurrentPage[] = [];

export function pushPage(
  builder: Component<{ page?: CurrentPage }>,
  options: {
    isModal?: boolean;
    event?: MouseEvent;
    alignment?: PageAlignment;
    offset?: {
      top?: number;
      left?: number;
      right?: number;
      bottom?: number;
    };
    class?: string;
    enterAnimation?: boolean;
    exitAnimation?: boolean;
    disableCloseButton?: boolean;
    preventClose?: boolean;
    path?: string;
    pushState?: boolean;
    eventProps?: Object;
  } = {},
  name: HubbleRoute
) {
  routerLog(
    `pushing ${name}. modal popping is ${isModalPopping}. Push history is ${options.pushState}`
  );
  const [isOpen, setIsOpen] = createSignal<boolean>(true);

  var x: number | undefined, y: number | undefined;

  if (options.event) {
    x = options.event.clientX;
    y = options.event.clientY;
  }

  const [isContentVisible, setIsContentVisible] = createSignal<boolean>(true);

  const disableCloseButton =
    (options.disableCloseButton == true && options.isModal == true) ||
    options.isModal != true;

  if (options.pushState ?? true) {
    history.pushState({}, "", "");
  }

  if (isModalPopping > 0) {
    pushedWhilePopping.push({
      builder,
      name: name,
      class: options.class,
      setIsContentVisible,
      isContentVisible,
      isModal: options.isModal == true,
      isOpen,
      setIsOpen,
      x,
      y,
      alignment: options.alignment,
      offset: options.offset,
      enterAnimation: options.enterAnimation,
      exitAnimation: options.exitAnimation,
      disableCloseButton: disableCloseButton,
      preventClose: options.preventClose,
      eventProps: options.eventProps,
    });
    return;
  }

  setPages([
    ...pages(),
    {
      builder,
      class: options.class,
      isContentVisible,
      setIsContentVisible,
      enterAnimation: options.enterAnimation,
      exitAnimation: options.exitAnimation,
      isModal: options.isModal == true,
      isOpen,
      setIsOpen,
      x,
      y,
      alignment: options.alignment,
      offset: options.offset,
      disableCloseButton: disableCloseButton,
      preventClose: options.preventClose,
      name: name,
      eventProps: options.eventProps,
    },
  ]);

  routerLog(`pages are ${pages().map((p) => p.name)}`);
  routerLog(`___________________________________________________________`);
}

export function popPage() {
  routerLog(`doing popPage`);
  if (pages().length == 0) return;
  setPages(pages().slice(0, -1));

  if (pushedWhilePopping.length > 0) {
    setPages([...pages(), ...pushedWhilePopping]);
    return;
  }

  isModalPopping -= 1;
  routerLog(
    `popping done. isModalPopping - ${isModalPopping} pages - ${pages().map((p) => p.name)}`
  );
  routerLog(`___________________________________________________________`);
}

let onPopCallBack = () => {};

onMount(() => {
  window.addEventListener("popstate", (event) => {
    if (event.state) {
      setTimeout(() => {
        // todo: this should be cleaned later
        try {
          onPopCallBack();
          onPopCallBack = () => {};
        } catch (error) {
          console.error(error);
        }
      }, 260);
    }
  });
});

export function goBack(callback?: () => void) {
  onPopCallBack = callback || (() => {});

  window.history.back();
  routerLog(`window.history.back complete`);
}

export function popWithAnimation() {
  routerLog(`doing popWithAnimation`);
  const modal = pages()[pages().length - 1];

  isModalPopping += 1;
  modal.setIsContentVisible(false);
  setTimeout(() => {
    popPage();
  }, 250);
}

export async function popUntilHome() {
  routerLog(`popping until home`);
  await goBackNTimes(pages().length - 1);
}

export async function goBackNTimes(n: number) {
  routerLog(`going back ${n} times`);
  detachPopStateListener();
  for (let i = 0; i < n; i++) {
    goBack();
    popStateListener();
  }
  await new Promise((resolve) => setTimeout(resolve, 300));
  attachPopStateListener();
}

export function ModalHost() {
  // set enterAnimation to false for last page after 250 ms
  createEffect(() => {
    if (pages().length > 0) {
      const lastPage = pages()[pages().length - 1];
      if (lastPage.enterAnimation ?? true) {
        setTimeout(() => {
          lastPage.enterAnimation = false;
        }, 250);
      }
    }
  });

  createEffect(() => {
    if (pages().length > 0) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflowY = "scroll";
    }
  });

  const displayPages = () => {
    const indexOfLastPage = pages().findLastIndex(
      (page) =>
        !page.isModal &&
        page.name != "coupons" &&
        page.name != HubbleRoute.PAYMENT_STATUS_POLLING
    );
    return [
      ...pages().filter((page, index) => index >= indexOfLastPage),
    ].filter(Boolean) as CurrentPage[];
  };
  return (
    <For each={displayPages()}>
      {(page, index) => {
        return <ModalLayout modal={page} />;
      }}
    </For>
  );
}

export function ModalLayout(props: { modal: CurrentPage }) {
  const hasPosition = props.modal.x != undefined && props.modal.y != undefined;

  function getProps() {
    const offsetTop = props.modal.offset?.top || 0;
    const offsetLeft = props.modal.offset?.left || 0;
    const offsetRight = props.modal.offset?.right || 0;
    const offsetBottom = props.modal.offset?.bottom || 0;

    switch (props.modal.alignment) {
      case PageAlignment.top:
        return {
          top: `${0 + offsetTop}px`,
          left: `${0 + offsetLeft}px`,
          right: `${offsetRight}px`,
          bottom: "auto",
        };
      case PageAlignment.bottom:
        return {
          top: "auto",
          left: `${0 + offsetLeft}px`,
          right: `${offsetRight}px`,
          bottom: `${0 + offsetBottom}px`,
        };
      case PageAlignment.left:
        return {
          top: `${0 + offsetTop}px`,
          left: `${0 + offsetLeft}px`,
          right: "auto",
          bottom: `${offsetBottom}px`,
        };
      case PageAlignment.right:
        return {
          top: `${0 + offsetTop}px`,
          left: "auto",
          right: `${0 + offsetRight}px`,
          bottom: `${offsetBottom}px`,
        };
      case PageAlignment.topRight:
        return {
          top: `${0 + offsetTop}px`,
          left: "auto",
          right: `${0 + offsetRight}px`,
          bottom: "auto",
        };
      case PageAlignment.topLeft:
        return {
          top: `${0 + offsetTop}px`,
          left: `${0 + offsetLeft}px`,
          right: "auto",
          bottom: "auto",
        };
      case PageAlignment.bottomRight:
        return {
          top: "auto",
          left: "auto",
          right: `${0 + offsetRight}px`,
          bottom: `${0 + offsetBottom}px`,
        };
      case PageAlignment.bottomLeft:
        return {
          top: "auto",
          left: `${0 + offsetLeft}px`,
          right: "auto",
          bottom: `${0 + offsetBottom}px`,
        };
      case PageAlignment.center:
        return {
          top: "50%",
          left: "50%",
          right: "auto",
          bottom: "auto",
          transform: "translate(-50%, -50%)",
        };
    }
  }

  const closeModal = (event: MouseEvent) => {
    event.stopPropagation();
    goBack();
  };
  return (
    <div
      style={{
        "background-color": props.modal.isModal ? "" : "white",
        "user-select": "none",
        "z-index": props.modal.zIndex,
      }}
      class={classNames(
        "modalBackdrop",
        props.modal.isContentVisible() ? "visible" : "closed",
        hasPosition ? "floating" : "normal",
        props.modal.isModal ? "modal" : "page",
        props.modal.enterAnimation === false ? "noEnter" : "enter",
        props.modal.exitAnimation === false ? "noExit" : "exit",
        props.modal.class
      )}
      onClick={props.modal.preventClose ? undefined : closeModal}
    >
      <Show when={!props.modal.disableCloseButton}>
        <div class="modalCloseHandle">
          <CloseButton
            class="no-box-shadow button-close"
            iconClass="button-close-icon"
          />
        </div>
      </Show>
      <div
        style={getProps() as any}
        class="modalContent"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        {props.modal.builder({ page: props.modal })}
      </div>
    </div>
  );
}

export function CloseButton(props: {
  class?: string;
  onClick?: (event: MouseEvent) => void;
  iconClass?: string;
}) {
  return (
    <div
      class={classNames(props.class, "closeButton")}
      onClick={props.onClick}
    >
      <CloseIcon class={props.iconClass} />
    </div>
  );
}

export enum HubbleRoute {
  // HOME
  HOME = "home",
  IFRAME_MODAL = "iframe_modal",
  HOME_MENU = "home_menu",

  // LOGIN
  COLLECT_PHONE = "collect_phone",
  COLLECT_OTP = "collect_otp",
  COMPLETE_PROFILE = "complete_profile",

  // SHOP
  COUPONS = "coupons",
  CHECKOUT_SUMMARY = "checkout_summary",
  BRAND_L2_SCREEN = "brand_l2",
  VOUCHER_EDIT_MODAL = "voucher_edit_modal",
  VOUCHER_GENERATION_SUCCESS = "voucher_generation_success",
  VOUCHER_GENERATION_FAIL = "voucher_generation_fail",
  VOUCHER_GENERATION_LOADER = "voucher_generation_loader",
  TRANSACTIONS = "transactions",
  TRANSACTION_L2 = "transaction_l2",
  HOW_TO_USE = "how_to_use",
  TNC = "tnc",
  BRAND_UNAVAILABLE = "brand_unavailable_modal",
  WARNING_MODAL = "warning_modal",

  // PAYMENTS
  PAYMENT_LOADER = "payment_loader",
  PAYMENT_OPTIONS = "payment_options",
  INVOICE = "invoice",
  UPI_ID_INPUT = "upi_id_input",
  PAYMENT_STATUS_POLLING = "payment_status_polling",

  // PHONE VERIFICATION
  PHONE_COLLECTION = "phone_collection",
  PHONE_OTP_VERIFICATION = "phone_otp_verification",

  COINS_TRANSACTIONS = "coins_transactions",
  WELCOME_MESSAGE = "welcome_message",
  CUSTOM_COIN_INPUT_MODAL = "custom_coin_input_modal",
  SEE_WHY_COINS_LIMIT_MODAL = "see_why_coins_limit_modal",

  HELP_AND_SUPPORT = "help_and_support",
  COLLECT_PHONE_NUMBER_GIFTING = "collect_phone_number_gifting",
  COLLECT_OTP_GIFTING = "collect_otp_gifting",
  WHATSAPP_SENT = "whatsapp_sent",

  BANK_TRANSFER = "bank_transfer",
  BANK_HOW_TO_TRANSFER = "bank_how_to_transfer_modal",
  BANK_SEE_WHY_BS = "bank_see_why_bs_modal",
  BANK_SUMMARY_TRANSFER = "bank_summary_transfer_modal",
  BANK_EDIT_AMOUNT = "bank_edit_amount",
  BANK_TRANSFER_SUCCESS = "bank_transfer_success",
  BANK_TRANSFER_FAIL = "bank_transfer_fail",
  BANK_TRANSFER_LOADER = "bank_transfer_loader",
  BANK_TRANSFER_MODE = "bank_transfer_mode",
  BANK_TRANSFER_PAYOUTS = "bank_transfer_payouts",
  BANK_TRANSFER_PENNY_DROP_SUCCESS = "bank_transfer_mode",
  BANK_TRANSFER_HIGH_WAITING_TIME = "bank_transfer_high_waiting_time",
}
